import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Typography,
  LinearProgress,
  styled,
  linearProgressClasses,
  TextareaAutosize,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import DOMPurify from 'dompurify';
import COLORS from 'theme/colors';

interface FreeTextRendererProps {
  question: any;
  handleNext: (payload: any) => void;
  openModal: () => void;
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === 'light' ? '#3467ff' : '#308fe8',
  },
}));

const FreeTextRenderer: React.FC<FreeTextRendererProps> = ({ question, handleNext, openModal }) => {
  const [answer, setAnswer] = useState('');
  const [showError, setShowError] = useState(false);
  const [charError, setCharError] = useState('');
  const rows = question?.maxRows ?? 5;
  const maxChars = rows === 5 ? 500 : 50;
  const isNextDisabled = answer.length > maxChars;
  const printOptional: string = question?.type === 'optional' ? '(Optional)' : '';

  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));

  const handleBackButton = () => {
    handleNext({
      event: 'back',
      launchType: question.launchType,
    });
  };

  const onSubmit = () => {
    if (answer.length > maxChars) {
      setCharError(`Maximum ${maxChars} characters allowed.`);
      return;
    }

    if (question?.type === 'mandatory' && !answer.trim()) {
      setShowError(true);
      return;
    }

    setShowError(false);

    const payload = {
      quizId: question.questionId,
      answers: [{ input: answer }],
      answerType: question.answerMode,
    };
    handleNext(payload);
  };

  const validateMaxChars = (value: string) => {
    setAnswer(value);
    if (value.length > 500) {
      setCharError('Maximum 500 characters allowed.');
      return;
    }

    if (charError) {
      setCharError('');
    }

    if (showError) {
      setShowError(false);
    }
  };

  return (
    <Grid container maxWidth='100%'>
      <Box
        sx={{
          position: {
            xs: 'inherit',
            md: 'absolute',
          },
          left: '20px',
          top: '130px',
        }}
      >
        <Button
          sx={{
            background: COLORS.LightBlue,
            minWidth: 140,
            mb: 1,
          }}
          variant='outlined'
          startIcon={<ArrowBackIcon />}
          onClick={handleBackButton}
          disabled={question?.event === 'back'}
        >
          Back
        </Button>
      </Box>

      <Grid item xs={12}>
        <BorderLinearProgress value={question?.progress} variant='determinate' />

        {question?.header}

        <Container
          sx={{
            background: '#fff',
            padding: '20px 40px',
            borderRadius: '12px',
          }}
        >
          <Typography
            sx={{
              color: '#6941C6',
              fontSize: '18px',
              fontWeight: 500,
              mb: '24px',
              lineHeight: '28px',
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(question?.heading),
              }}
            />
          </Typography>

          {question?.placementOfPrompt === null && (
            <Typography
              mb={3}
              sx={{
                lineHeight: '28px',
                mt: '5px',
                mb: '24px',
                fontSize: '18px',
                fontWeight: 500,
                color: '#2C3446',
                fontStyle: 'italic',
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.prompt) }} />{' '}
            </Typography>
          )}

          <Typography
            sx={{
              lineHeight: '30px',
              mb: '20px',
              fontSize: '20px',
              fontWeight: 500,
              color: '#2C3446',
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(question?.question),
              }}
            />
            <span style={{ fontWeight: 'bold', fontSize: '18px' }}> {printOptional}</span>
          </Typography>

          {question?.placementOfPrompt === 'afterQuiz' && (
            <Typography
              mb={3}
              sx={{
                lineHeight: '28px',
                mt: '5px',
                mb: '24px',
                fontSize: '18px',
                fontWeight: 500,
                color: '#2C3446',
                fontStyle: 'italic',
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question?.prompt) }} />{' '}
            </Typography>
          )}

          {question?.informationSection && (
            <Box mb={4}>
              <Alert severity='info'>
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(question.informationSection),
                  }}
                />
              </Alert>
            </Box>
          )}

          {question?.warn && (
            <Box mb={4}>
              <Grid
                container
                sx={{
                  background: '#F8F5FF',
                  borderRadius: '8px',
                  p: 2,
                }}
              >
                <Grid item xs={0.5}>
                  <WarningAmberIcon color='error' />
                </Grid>

                <Grid item xs={11.5}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(question.warn),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {question?.info && (
            <Box
              sx={{
                background: '#F8F5FF',
                p: 2,
                borderRadius: '8px',
                mb: 4,
              }}
            >
              <Grid container>
                <Grid item xs={0.5}>
                  <InfoOutlinedIcon />
                </Grid>

                <Grid item xs={11.5}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(question.info),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 3, clear: 'both' }}>
            <Box>
              <Box mt={1} display='flex' justifyContent='space-between'>
                <Typography variant='caption'>
                  {answer.length}/{maxChars} characters
                </Typography>
              </Box>
              <Box mt={1}>
                <TextareaAutosize
                  minRows={rows}
                  maxRows={isSmallDevice ? undefined : rows}
                  placeholder='Enter your response here...'
                  value={answer}
                  onChange={(e) => {
                    const value = e.target.value;
                    validateMaxChars(value);
                  }}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    lineHeight: '24px',
                    border: charError ? '1px solid #D92D20' : '1px solid #D0D5DD',
                    borderRadius: '0px',
                    resize: 'vertical',
                    outline: 'none',
                  }}
                />

                {showError && (
                  <Box mt={2}>
                    <Typography
                      sx={{
                        display: 'inline-block',
                        backgroundColor: '#D92D20',
                        color: '#FFFFFF',
                        px: 2,
                        py: 1,
                        borderRadius: '4px',
                        fontWeight: 500,
                      }}
                    >
                      This field is required.
                    </Typography>
                  </Box>
                )}
                {charError && (
                  <Typography
                    sx={{
                      color: '#D92D20',
                      fontSize: '14px',
                      mt: 1,
                    }}
                  >
                    {charError}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            mt={3}
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column', // mobile
                sm: 'row', // tablet/desktop
              },
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Button
              variant='contained'
              disabled={isNextDisabled}
              onClick={onSubmit}
              sx={{
                width: {
                  xs: '100%',
                  sm: '180px',
                },
                minHeight: 42,
              }}
            >
              Next
            </Button>

            <Button
              variant='outlined'
              onClick={openModal}
              sx={{
                width: {
                  xs: '100%',
                  sm: '180px',
                },
                minHeight: 42,
              }}
            >
              Continue Later
            </Button>
          </Box>
        </Container>
      </Grid>
    </Grid>
  );
};

export default FreeTextRenderer;
